/**
 * AreaCellPositionCache
 *
 * Caches grid track boundaries for efficient pixel-to-cell coordinate resolution.
 * Used by AreaCell Position Observable to convert mouse clientX/Y to cell indices.
 *
 * Design:
 * - Lazy rebuild: Cache is built on first use (resolveAreaCell), not at initialization
 * - Cumulative boundaries: [0, 40, 80, 120] for 40px tracks (efficient lookup)
 * - Linear scan: O(n) acceptable for n<100 tracks (KISS principle)
 * - Invalidation: Called on ResizeObserver events and scroll updates
 */

import { resolveTrackSizes } from '../utils/resolve-grid-cell-index';

export class AreaCellPositionCache {
    private boundariesX: number[] | null = null;
    private boundariesY: number[] | null = null;

    constructor(private areaElement: HTMLElement) {}

    /**
     * Resolves the area-cell coordinates for a given viewport-relative position.
     *
     * @param clientX - Viewport-relative X coordinate (from MouseEvent.clientX)
     * @param clientY - Viewport-relative Y coordinate (from MouseEvent.clientY)
     * @returns Cell indices relative to area viewport, or null if out of bounds
     */
    resolveAreaCell(clientX: number, clientY: number): {
        areaCellX: number | null;
        areaCellY: number | null;
    } {
        // Lazy rebuild: build cache on first use
        if (!this.boundariesX || !this.boundariesY) {
            this.rebuild();
        }

        // Convert viewport-relative clientX/Y to area-relative pixel coordinates
        // getBoundingClientRect() called per invocation (not cached) — area position changes on page scroll without invalidate()
        const rect = this.areaElement.getBoundingClientRect();
        const localX = clientX - rect.left;
        const localY = clientY - rect.top;

        const areaCellX = this.findCellIndex(this.boundariesX!, localX);
        const areaCellY = this.findCellIndex(this.boundariesY!, localY);

        return { areaCellX, areaCellY };
    }

    /**
     * Invalidates the cached boundaries.
     * Next resolveAreaCell call will trigger a rebuild.
     */
    invalidate(): void {
        this.boundariesX = null;
        this.boundariesY = null;
    }

    /**
     * Rebuilds the boundary caches from current grid template.
     * Called lazily on first resolveAreaCell after invalidation.
     */
    private rebuild(): void {
        const style = getComputedStyle(this.areaElement);
        const columnsTemplate = style.gridTemplateColumns;
        const rowsTemplate = style.gridTemplateRows;

        // Parse track sizes and create cumulative boundary arrays
        // [0, 40, 80, 120, ...] for 40px tracks
        this.boundariesX = this.parseToCumulativeBoundaries(columnsTemplate, 'columns');
        this.boundariesY = this.parseToCumulativeBoundaries(rowsTemplate, 'rows');
    }

    /**
     * Finds the cell index for a given pixel coordinate.
     * Uses linear scan (KISS principle, O(n) acceptable for n<100).
     *
     * @param boundaries - Cumulative boundary array [0, 40, 80, 120, ...]
     * @param pixel - Pixel coordinate relative to area element
     * @returns Cell index, or null if out of bounds
     */
    private findCellIndex(boundaries: number[], pixel: number): number | null {
        // Linear scan (n < 100 tracks in practice, KISS principle)
        for (let i = 0; i < boundaries.length - 1; i++) {
            const currentBoundary = boundaries[i];
            const nextBoundary = boundaries[i + 1];
            if (currentBoundary !== undefined && nextBoundary !== undefined) {
                if (pixel >= currentBoundary && pixel < nextBoundary) {
                    return i;
                }
            }
        }
        return null;
    }

    /**
     * Parses grid template string to cumulative boundaries.
     * Uses resolveTrackSizes for track size extraction.
     *
     * @param template - CSS grid template string (gridTemplateColumns or gridTemplateRows)
     * @param axis - 'columns' or 'rows'
     * @returns Cumulative boundary array [0, 40, 80, 120, ...]
     */
    private parseToCumulativeBoundaries(template: string, axis: 'columns' | 'rows'): number[] {
        // Use resolveTrackSizes() exported from resolve-grid-cell-index.ts
        // (handles both regular grid and subgrid elements)
        // Returns track sizes [40, 40, 40] which we convert to cumulative boundaries [0, 40, 80, 120]
        // Cumulative boundaries chosen for efficient lookup:
        // One array access per query vs running sum calculation each time
        // Alternative: Track-sizes [40, 40, 40] would need running sum → O(n) per lookup
        const sizes = resolveTrackSizes(template, this.areaElement, axis);
        if (sizes === null) return [0];

        const boundaries = [0];
        sizes.forEach(size => {
            const lastBoundary = boundaries[boundaries.length - 1];
            if (lastBoundary !== undefined) {
                boundaries.push(lastBoundary + size);
            }
        });
        return boundaries;
    }
}
